import { IParsedChartDataRecord } from '../../interfaces';
import useCacheLlmRawDataEffect from './useCacheLlmRawDataEffect';
import { useMemo } from 'react';
import { escapeRegExp, isNil } from 'lodash';
import { MaybeNil } from '../../../../utils/interfaces';

interface IProps {
  chartTypeName: string;
  analysisId: string;
  parsedChartDataRecords: IParsedChartDataRecord[];
  skip?: boolean;
}

// XXX extract <prefix>NN matches separated from the rest by _ or - (or begining/end of string)
const extractNumberedMeasureKeysRegex = (prefix: string) =>
  RegExp(`(^|[_-])(${escapeRegExp(prefix)}\\d+)([_-]|$)`);

const CLEANUP_MEASURE_KEY_RX = /[-_]/g;

const extractNumberedMeasureKeys = (allKeys: string[], keyPrefix: string) => {
  const regex = extractNumberedMeasureKeysRegex(keyPrefix);
  const ret = new Set<string>();
  allKeys.forEach((dataKey) => {
    const matches = regex.exec(dataKey);
    if (!!matches) {
      matches.forEach((value) => {
        const clean = value.replaceAll(CLEANUP_MEASURE_KEY_RX, '');
        if (!!clean) {
          ret.add(clean);
        }
      });
    }
  });
  return [...ret];
};

const getAbbreviationsStrFromData = (
  record: MaybeNil<IParsedChartDataRecord>,
): string => {
  if (!record) {
    return '';
  }
  const ret: string[] = [];
  const isAbbreviation = (arr: string[]) =>
    arr.length === 1
      ? { abbrev: 'is internal abbreviation', suffix: '' }
      : { abbrev: 'are internal abbreviations', suffix: 's' };
  const keys = Object.keys(record);

  // discover mX measures
  const mMeasure = keys.includes('m') ? ['m'] : [];
  const mXMeasures = extractNumberedMeasureKeys(keys, 'm');
  const mAllMeasures = [...mMeasure, ...mXMeasures];
  if (mAllMeasures.length > 0) {
    const { abbrev, suffix } = isAbbreviation(mAllMeasures);
    ret.push(
      `${mAllMeasures.join(', ')} ${abbrev} for the formula result measure${suffix}`,
    );
  }

  // discover smX measures
  const smXMeasures = extractNumberedMeasureKeys(keys, 'sm');
  if (smXMeasures.length > 0) {
    const { abbrev, suffix } = isAbbreviation(smXMeasures);
    ret.push(
      `${smXMeasures.join(', ')} ${abbrev} for the included simple measure${suffix})`,
    );
  }
  if (ret.length === 0) {
    return '';
  }
  return ` where ${ret.join(' and ')}`;
};

const useCacheParsedChartLlmRawDataEffect = ({
  chartTypeName,
  analysisId,
  parsedChartDataRecords,
  skip,
}: IProps) => {
  const rawData = useMemo(() => {
    if (parsedChartDataRecords.length === 0) {
      return null;
    }
    const abbreviationsStr = getAbbreviationsStrFromData(
      parsedChartDataRecords[0],
    );
    const ret = [
      `The ${chartTypeName} chart was produced from the following raw data${abbreviationsStr}:`,
    ];
    parsedChartDataRecords.forEach((d) => {
      const keys = Object.keys(d);
      const row = keys.flatMap((key) => {
        const value = d[key];
        return !isNil(value) ? [`${key}=${value}`] : [];
      });
      ret.push(row.join(', '));
    });
    return ret.join('\n');
  }, [chartTypeName, parsedChartDataRecords]);

  useCacheLlmRawDataEffect({
    analysisId,
    rawData,
    skip,
  });
};

export default useCacheParsedChartLlmRawDataEffect;
